<script setup lang="ts">
import {Button, Checkbox, Icon} from "@/Components/Library";
import CoverImage from "../Feed/CoverImage.vue";
import {useRouter} from "vue-router";
import {useMainStore} from "@/Stores/store";
import {Question as QuestionType} from "@/Types/question.d";
import Question from "@/Models/Question";
import dayjs from "dayjs";
import Str from "@/Utils/Str";
import User from "@/Models/User";

const router = useRouter();
const $store = useMainStore();
const $user = new User();

const {question: questionProp, showMenu = false} = defineProps<{
    question: QuestionType;
    showMenu?: boolean;
}>();

const question = new Question(questionProp);

const emit = defineEmits<{
    toggleQuestionMenu: (question: any) => void;
}>();
</script>
<template>
    <div
        class="relative flex rounded-lg border border-stone-300 bg-white transition-all dark:border-stone-800 dark:bg-stone-900"
        :class="{
            'border-t-4 !border-t-red-600':
                question.isArchived ||
                !question.get('isSelectable') ||
                (router.currentRoute.value.query.select && question.user?.id !== $user.id),
        }"
        v-auto-animate
    >
        <div
            v-if="router.currentRoute.value.query.select && !question.isArchived && question.user?.id === $user.id"
            class="absolute z-10 m-1 rounded-lg bg-white p-1"
        >
            <Checkbox v-model="$store.selectedQuestions" :value="question.id" class="scale--150" />
        </div>
        <div
            v-if="router.currentRoute.value.query.launch && question.get('isSelectable')"
            class="absolute z-10 m-1 rounded-lg bg-white p-1"
        >
            <Checkbox v-model="$store.launch.questions" :value="question.id" class="scale--150" />
        </div>
        <component
            :is="question.isArchived ? 'div' : 'router-link'"
            class="flex w-full grow flex-col"
            :to="{
                name: 'questions.preview',
                params: {question: question.id},
                query: router.currentRoute.value.query,
            }"
        >
            <div class="relative w-full flex-none rounded-t-md">
                <CoverImage v-if="question.hasCoverImage" :question="question" placement="top" />
                <div
                    v-else
                    class="flex aspect-video w-full items-center justify-center bg-stone-100 dark:bg-stone-800/50"
                >
                    <p class="text-xs text-stone-400">No image</p>
                </div>
                <Icon
                    v-if="question.isArchived"
                    icon="ri:archive-fill"
                    width="32"
                    class="absolute left-2 top-1.5 text-red-600"
                />
                <div
                    v-if="question.user"
                    class="absolute top-2.5 z-50 flex items-center rounded px-2 py-1 text-xs font-medium transition-all"
                    :class="[
                        question.user?.profile?.color?.class !== 'white' ? 'text-white' : 'text-black',
                        `bg-${question.user?.profile?.color?.class ?? 'askr-green'}`,
                        (router.currentRoute.value.query.launch && question.get('isSelectable')) ||
                        (router.currentRoute.value.query.select && question.user?.id === $user.id) ||
                        question.isArchived
                            ? 'left-12'
                            : 'left-2',
                    ]"
                >
                    <Icon v-if="question.user.id === $user.id" icon="prime:user" width="14" class="mr-1" />
                    {{ question.user?.name }}
                </div>
            </div>
            <div class="relative flex grow flex-col px-4 py-2 transition">
                <div v-if="showMenu" class="absolute right-0 top-0 p-1.5">
                    <Button
                        :icon="{
                            icon: 'pepicons-pencil:dots-y',
                            width: '24',
                        }"
                        class="!ring-0"
                        @click.stop.prevent="emit('toggleQuestionMenu', $event, question)"
                    />
                </div>
                <div class="mb--1 flex items-center gap-2">
                    <h6 class="text-[0.675rem] font-semibold uppercase">
                        {{
                            question.type?.slug === "text" && question.params.allow_annotations
                                ? "Annotation"
                                : question.type.name
                        }}
                    </h6>
                    <span
                        v-if="question.type?.slug === 'gatekeeper'"
                        class="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-800"
                    >
                        KEY
                    </span>
                </div>
                <h2 class="mb-2 pr-3 text-xs font-bold lg:text-sm">
                    {{ Str.of(question.title).truncate() }}
                </h2>
                <div class="mb-2 mt-auto flex gap-2">
                    <span class="flex items-center gap-1 text-sm">
                        <Icon icon="prime:users" width="18" />
                        {{ question.get("groups_count") }}
                    </span>
                    <span class="flex items-center gap-1 text-sm">
                        <Icon icon="gg:check-r" width="14" />
                        {{ question.get("responses_count") }}
                    </span>
                </div>
                <div>
                    <span class="flex items-center gap-1 text-sm">
                        <Icon icon="ic:round-edit-calendar" width="18" />
                        {{ dayjs(question.get("updated_at")).format("DD/MM/YYYY HH:mm") }}
                    </span>
                </div>
            </div>
        </component>
    </div>
</template>
<style scoped></style>
